/**
 * Who can see and edit a skill. Mirrors the backend `SkillAccessibility` enum:
 * - `"private"`: only the author can see, edit, or invoke it.
 * - `"org"`: visible to every org member; author-only to edit.
 * - `"org-editable"`: visible to every org member, and editable by any member
 *   who has subscribed to it. Changing accessibility and deleting the whole
 *   skill stay author-only.
 */
export type SkillAccessibility = "private" | "org" | "org-editable";

/**
 * Pattern that ``Skill.create()`` and ``UpdateSkillMetadataRequest.name`` enforce
 * server-side. Mirrors ``SKILL_NAME_PATTERN`` in the Python SDK exactly. Spaces
 * are deliberately excluded so a name is unambiguously bounded inside the chat
 * composer's ``/slug`` syntax.
 */
export const SKILL_NAME_PATTERN = /^[A-Za-z0-9_-]+$/;
export const SKILL_NAME_MAX_LENGTH = 120;

/**
 * Max length of a skill version's ``description`` (the "when to use" text),
 * matching the server-side ``description`` column limit. Shared so the chat
 * tool's validation and the editor's input cap can't drift apart.
 */
export const SKILL_DESCRIPTION_MAX_LENGTH = 500;

/**
 * Validate a candidate skill name against the create / rename rules. Returns a
 * user-facing error message when invalid, or ``null`` when the name is
 * acceptable. Trim ``name`` before calling — leading / trailing whitespace
 * counts as a violation under the no-spaces rule.
 */
export function validateSkillName(name: string): string | null {
  if (name.length === 0) {
    return "Name is required.";
  }
  if (name.length > SKILL_NAME_MAX_LENGTH) {
    return `Name must be ≤ ${SKILL_NAME_MAX_LENGTH} characters.`;
  }
  if (/\s/.test(name)) {
    return "Name cannot contain spaces — use hyphens or underscores instead.";
  }
  if (!SKILL_NAME_PATTERN.test(name)) {
    return "Name can only contain letters, digits, hyphens, and underscores.";
  }
  return null;
}

/**
 * Validate a skill's ``description`` (when-to-use text) against the create /
 * edit rules. Returns a user-facing error message when invalid, or ``null``
 * when acceptable. Trim ``description`` before calling — a value that is only
 * whitespace counts as empty.
 */
export function validateSkillDescription(description: string): string | null {
  if (description.length === 0) {
    return "Description is required.";
  }
  if (description.length > SKILL_DESCRIPTION_MAX_LENGTH) {
    return `Description must be ≤ ${SKILL_DESCRIPTION_MAX_LENGTH} characters.`;
  }
  return null;
}

/**
 * Caller-relative slice of the visible skills, mirrors the backend's
 * ``SkillListScope`` enum:
 * - ``"personal"``: skills the caller authored or subscribed to.
 * - ``"org"``: org-shared skills the caller did not author (regardless of
 *   subscription state).
 */
export type SkillListScope = "personal" | "org";

export interface SkillRecord {
  skill_id: string;
  org_id: string;
  accessibility: SkillAccessibility;
  name: string;
  /** Free-form labels for categorization; edited via put_tags / remove_tags. */
  tags: string[];
  created: string;
  created_by: string;
  modified: string;
}

export interface SkillVersionRecord {
  skill_id: string;
  version: number;
  description: string;
  body: string;
  /**
   * Topic names this version's procedure investigates (e.g. `/imu/data`).
   * Extracted client-side from the body's `/topic` references. When the chat
   * AI loads the skill with a dataset in scope it resolves these to topic
   * schemas in the same tool result. Version-scoped content, not skill
   * metadata.
   */
  relevant_topics: string[];
  created: string;
  modified: string;
}

/**
 * Per-user state for a single skill.
 *
 * Exists for any skill the user has authored or explicitly subscribed to. The
 * nullable `ai_version` pins which version (if any) the user has exposed to the
 * AI's auto-invoke registry. `null` means subscribed but AI-disabled; manual
 * chip-invocation still works.
 */
export interface SkillSubscriptionRecord {
  user_id: string;
  skill_id: string;
  ai_version: number | null;
  subscribed: string;
}

export interface SkillSummary {
  skill: SkillRecord;
  latest_version: SkillVersionRecord | null;
  /** Caller's subscription row, if any. `null` means the skill is in the Org tab. */
  subscription: SkillSubscriptionRecord | null;
}

export interface CreateSkillRequest {
  name: string;
  accessibility: SkillAccessibility;
  description: string;
  body: string;
  /** Initial set of tags. Omit or pass [] to start untagged. */
  tags?: string[];
  /**
   * Topic names v1's procedure investigates. Omit or pass [] for none. See
   * {@link SkillVersionRecord.relevant_topics}.
   */
  relevant_topics?: string[];
}

export interface CreateSkillVersionRequest {
  description: string;
  body: string;
  /** Topic names this version investigates. Omit or pass [] for none. */
  relevant_topics?: string[];
}

export interface UpdateSkillVersionRequest {
  description?: string;
  body?: string;
  /**
   * New topic-name list. Omit to leave unchanged; pass [] to clear. See
   * {@link SkillVersionRecord.relevant_topics}.
   */
  relevant_topics?: string[];
}

export interface UpdateSkillMetadataRequest {
  name?: string;
  accessibility?: SkillAccessibility;
  /** Tags to add. Existing values are merged + deduped server-side. */
  put_tags?: string[];
  /** Tags to remove. Missing values are silently ignored. */
  remove_tags?: string[];
}

export interface SetSkillSubscriptionRequest {
  /** Pinned AI-available version. `null` means subscribed but AI-disabled. */
  ai_version: number | null;
}

/**
 * Whether `userId` may edit the given skill's *content* — its version bodies /
 * descriptions, the addition or deletion of versions, and the skill's name and
 * tags. True for the author of any skill, and for any member who has
 * subscribed to an `org-editable` skill.
 *
 * This deliberately does NOT cover the two ownership-level mutations —
 * changing accessibility and deleting the whole skill — which stay author-only
 * even on an `org-editable` skill. Gate those on `created_by === userId`
 * directly.
 */
export function canEditSkill(summary: SkillSummary, userId: string): boolean {
  if (summary.skill.created_by === userId) {
    return true;
  }
  return (
    summary.skill.accessibility === "org-editable" &&
    summary.subscription !== null
  );
}

/**
 * Whether `userId` is *definitely* barred from editing `skill`, judged from a
 * bare record.
 *
 * {@link canEditSkill} needs a {@link SkillSummary} because `org-editable`
 * turns on the caller's subscription, which a lone record doesn't carry. The
 * by-name lookup `client_create_skill` uses returns only a record, so this
 * answers the part that record can settle: edits to a plain `org` skill are
 * author-only, whatever the caller subscribes to. `org-editable` stays
 * undecidable here and is settled by `canEditSkill` once the Skills page has
 * the summary; another user's `private` skill never comes back from the
 * lookup at all.
 *
 * Deliberately one-directional — `false` means "not provably barred", not
 * "editable". Treating it as permission would grant edits the server then
 * refuses.
 */
export function isSkillEditDefinitelyBarred(
  skill: SkillRecord,
  userId: string,
): boolean {
  return skill.created_by !== userId && skill.accessibility === "org";
}

/**
 * Resolve a skill *name* to a summary `userId` may edit, or `null`.
 *
 * The `client_create_skill` update handoff targets a skill by name, since
 * that is the only identifier the AI sees in the skill registry. Names are
 * unique per scope rather than per org, so a bare name can match both a
 * private and an org skill: an editable match wins over a
 * visible-but-read-only one, and the caller treats `null` as "tell the user,
 * don't create something new".
 *
 * When *both* candidates are editable — the caller owns a private `foo` while
 * the org also has an `org-editable` `foo` they subscribe to — the caller's
 * own private skill wins. That mirrors `SkillsRepo.get_skill_by_name`
 * (`ORDER BY (accessibility = 'private') DESC`) and {@link resolveSlugs}, so
 * a bare name resolves to the same skill however it was looked up. Without
 * the tie-break the winner would fall out of list order, which is
 * `modified DESC` — "whichever was edited most recently", a criterion no
 * caller asked for.
 *
 * Matching is case-sensitive: the AI is given exact registry names, so a
 * near-miss is more likely a hallucinated name than a casing slip, and
 * failing is safer than silently editing a similarly-named skill.
 */
export function resolveEditableSkillByName(
  summaries: readonly SkillSummary[],
  name: string,
  userId: string,
): SkillSummary | null {
  let match: SkillSummary | null = null;
  for (const summary of summaries) {
    if (summary.skill.name !== name || !canEditSkill(summary, userId)) {
      continue;
    }
    if (summary.skill.accessibility === "private") {
      return summary;
    }
    match ??= summary;
  }
  return match;
}
